import { useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';

type AuditRow = {
  id: string;
  entity: string;
  entityId: string;
  action: string;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  userId?: string | null;
  userName?: string | null;
};

export default function AuditActivityPage() {
  const { fetchJson } = useApiClient();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [entity, setEntity] = useState('');
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (entity) params.set('entity', entity);
      if (action) params.set('action', action);
      const payload = await fetchJson<{ data: AuditRow[] }>(`/audit/activity?${params.toString()}`);
      setRows(payload.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load audit activity.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Audit</p>
          <h1 className="text-2xl font-semibold text-slate-900">Activity tracking</h1>
        </div>
        <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium">
          <RotateCcw size={16} />
          Refresh
        </button>
      </div>
      <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-3">
        <input value={entity} onChange={(event) => setEntity(event.target.value)} placeholder="Entity filter" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
        <input value={action} onChange={(event) => setAction(event.target.value)} placeholder="Action filter" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
        <button type="button" onClick={() => void load()} className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Apply filters</button>
      </div>
      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3">When</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Entity</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td className="px-4 py-5 text-slate-500" colSpan={4}>Loading audit activity...</td></tr> : rows.length ? rows.map((row) => (
              <tr key={row.id}>
                <td className="px-4 py-3 text-slate-600">{new Date(row.createdAt).toLocaleString()}</td>
                <td className="px-4 py-3 text-slate-600">{row.userName || row.userId || 'System'}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{row.action}</td>
                <td className="px-4 py-3 text-slate-600">{row.entity} · {row.entityId}</td>
              </tr>
            )) : <tr><td className="px-4 py-5 text-slate-500" colSpan={4}>No audit activity found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
